import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { incomeAPI } from '../api';
import useAuthStore from '../store/auth';
import { formatAmount } from '../utils/currency';

const SOURCES = ['Salaire','Business','Don','Investissement','Location','Autre'];

export default function IncomePage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const currency = user?.currency || 'EUR';
  const fmt = n => formatAmount(n, currency);

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [month, setMonth]       = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm]         = useState({ amount:'', source:'Salaire', date:new Date().toISOString().split('T')[0], description:'' });

  useEffect(() => { load(); }, [month]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await incomeAPI.list({ month }); setItems(data); }
    catch { toast.error(t('error')); }
    finally { setLoading(false); }
  };

  const openAdd  = () => { setEditing(null); setForm({ amount:'', source:'Salaire', date:new Date().toISOString().split('T')[0], description:'' }); setShowModal(true); };
  const openEdit = (item) => { setEditing(item); setForm({ amount:item.amount, source:item.source, date:item.date?.split('T')[0], description:item.description || '' }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.amount || !form.date) return toast.error(t('required'));
    try {
      if (editing) { await incomeAPI.update(editing.id, form); toast.success(t('income_saved')); }
      else         { await incomeAPI.create(form);             toast.success(t('income_saved')); }
      setShowModal(false); load();
    } catch { toast.error(t('error')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await incomeAPI.remove(id); toast.success(t('income_deleted')); load(); }
    catch { toast.error(t('error')); }
  };

  const total = items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const inp = { width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', color:'var(--text)', fontSize:14, outline:'none', boxSizing:'border-box' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontFamily:'Syne,sans-serif', fontSize:24, fontWeight:700 }}>{t('income')}</h1>
          <p style={{ color:'var(--text2)', fontSize:14, marginTop:4 }}>Total : <strong style={{ color:'var(--accent3)' }}>{fmt(total)}</strong></p>
        </div>
        <button onClick={openAdd} style={{ padding:'10px 20px', background:'var(--accent3)', color:'#0a2a1a', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, cursor:'pointer' }}>
          + {t('add')}
        </button>
      </div>

      <div style={{ marginBottom:20 }}>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', color:'var(--text)', fontSize:14, outline:'none' }} />
      </div>

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
        {loading ? <p style={{ color:'var(--text2)', textAlign:'center', padding:40 }}>{t('loading')}</p>
        : items.length === 0 ? (
          <div style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📈</div>
            <p>{t('no_income')}</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>{[t('date'),t('source'),t('description'),t('amount'),t('actions')].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'10px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:12, color:'var(--text2)', fontSize:14 }}>{new Date(item.date).toLocaleDateString('fr-FR')}</td>
                    <td style={{ padding:12 }}><span style={{ padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600, background:'#6af7b820', color:'var(--accent3)' }}>{item.source}</span></td>
                    <td style={{ padding:12, fontSize:14, color:'var(--text2)' }}>{item.description || '-'}</td>
                    <td style={{ padding:12, fontFamily:'Syne,sans-serif', fontWeight:700, color:'var(--accent3)' }}>+{fmt(item.amount)}</td>
                    <td style={{ padding:12 }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => openEdit(item)} style={{ padding:'5px 10px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:12, cursor:'pointer' }}>{t('edit')}</button>
                        <button onClick={() => handleDelete(item.id)} style={{ padding:'5px 10px', background:'var(--danger)', color:'#fff', border:'none', borderRadius:8, fontSize:12, cursor:'pointer' }}>{t('delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000090', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20, padding:32, width:'100%', maxWidth:480 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h2 style={{ fontFamily:'Syne,sans-serif', fontWeight:700 }}>{editing ? t('edit_income') : t('add_income')}</h2>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'var(--text2)', cursor:'pointer' }}>×</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('amount')}</label>
                <input type="number" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('source')}</label>
                <select value={form.source} onChange={e => setForm({...form, source:e.target.value})} style={inp}>
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('date')}</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} style={inp} />
            </div>
            <div style={{ marginBottom:24 }}>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('description')}</label>
              <textarea value={form.description} onChange={e => setForm({...form, description:e.target.value})} style={{ ...inp, minHeight:80, resize:'vertical' }} />
            </div>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => setShowModal(false)} style={{ flex:1, padding:14, background:'var(--surface2)', color:'var(--text)', border:'1px solid var(--border)', borderRadius:10, cursor:'pointer' }}>{t('cancel')}</button>
              <button onClick={handleSave} style={{ flex:1, padding:14, background:'var(--accent3)', color:'#0a2a1a', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, cursor:'pointer' }}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}